import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	ContentWithSidebar,
	Dialog,
	DialogBackdropState,
	SidebarHandle
} from "client/Components";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback,
	useSynchronizedRef,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	isNil
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	HubManagementDialogSidebar,
	listAvailableSidebarItems,
	SidebarItem
} from "./HubManagementDialogSidebar";
import { never } from "client_legacy/ndaw/errors";
import { AnnouncementsCategories } from "./Content/AnnouncementsContent/AnnouncementsCategories";
import { AnnouncementsOverview } from "./Content/AnnouncementsContent/AnnouncementsOverview";
import { EventsCategories } from "./Content/EventsContent/EventsCategories";
import { EventsOverview } from "./Content/EventsContent/EventsOverview";
import { KnowledgeItemsCategories } from "./Content/KnowledgeItemsContent/KnowledgeItemsCategories";
import { KnowledgeItemsOverview } from "./Content/KnowledgeItemsContent/KnowledgeItemsOverview";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { getHubManagement, HubManagementViewModel } from "client/Api/Hub/hubSettings";
import { HttpRequestError } from "client/Api/httpClient";
import { TemplateOverview } from "./Content/Templates/TemplateOverview";
import { CommunitiesContent } from "./Content/CommunitiesContent";
import { HubEvent, publish } from "../../events";
import { CommunitiesSettings } from "./Content/CommunitiesSettings";
import { CommunityMembershipRequests } from "./Content/CommunityMembershipRequests";
import { CommunitiesSettingsModel } from "client/Api/Communities/communitiesSettings";
import { HubSettings } from "./Content/Settings/HubSettings";

export type SelectedView =
	| "settings"
	| "announcement.overview"
	| "announcement.category"
	| "announcement.template"
	| "communities"
	| "communities.settings"
	| "communities.membershipRequests"
	| "event.overview"
	| "event.category"
	| "event.template"
	| "knowledgeItem.overview"
	| "knowledgeItem.category"
	| "knowledgeItem.template";

export type HubManagementDialogProps = {
	onClose: () => void;
	initialView?: SelectedView;
};

export const HubManagementDialog = (props: HubManagementDialogProps): ReactElement => {
	const {
		onClose,
		initialView
	} = props;

	const [isHierarchyChanged, setIsHierarchyChanged] = useState<boolean>(false);
	const [changedItemKinds, setChangedItemKinds] = useState<ReadonlySet<HubItemKind>>(new Set());
	const [backdropState, setBackdropState] = useState<DialogBackdropState>();
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [selectedView, setSelectedView] = useState<SelectedView>();
	const [viewModel, setViewModel] = useState<HubManagementViewModel | null>(null);
	const [availableSidebarItems, setAvailableSidebarItems] = useState<readonly SidebarItem[]>(EMPTY_ARRAY);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const sidebarRef: RefObject<SidebarHandle | null> = useRef<SidebarHandle>(null);
	const alertManager: AlertManager = useAlerts();

	const initialViewRef: ReadonlyRefObject<SelectedView | undefined> = useSynchronizedRef(initialView);

	const handleSelectedViewChange = useCallback(
		(selectedView: SelectedView): void => {
			setSelectedView(selectedView);

			if (isMobile) {
				sidebarRef.current
					?.onClose();
			}
		},
		[isMobile]
	);

	const stableOnClose: () => void = useStableImperativeCallback(onClose);

	useEffect(
		(): EffectWithDestructorResult => {
			const fetchData = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const viewModel: HubManagementViewModel = await getHubManagement(signal);
					setViewModel(viewModel);
					const availableSidebarItems: readonly SidebarItem[] = listAvailableSidebarItems(viewModel);

					setAvailableSidebarItems(availableSidebarItems);

					setSelectedView(
						getInitialSelectedView(
							initialViewRef.current,
							availableSidebarItems
						)
					);

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					if (e instanceof HttpRequestError && e.statusCode === 403) {
						alertManager.showError(
							localize("_YouDontHavePermissionsToViewThisPage")
						);

						stableOnClose();
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					stableOnClose();
				}
			};

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			initialViewRef,
			stableOnClose
		]
	);

	const closeHandler = useCallback(
		(): void => {
			if (isHierarchyChanged) {
				publish(HubEvent.HierarchyChanged, undefined);
			}

			if (changedItemKinds.size > 0) {
				publish(
					HubEvent.ItemsChanged,
					{
						changedItemKinds
					}
				);
			}

			onClose();
		},
		[
			isHierarchyChanged,
			changedItemKinds,
			onClose
		]
	);

	const hierarchyChangedHandler = useCallback(
		(): void => {
			setIsHierarchyChanged(true);
		},
		[]
	);

	const itemsChangedHandler = useCallback(
		(kind: HubItemKind): void => {
			setChangedItemKinds(
				prevState => {
					const newSet: Set<HubItemKind> = new Set(prevState);

					return newSet.add(kind);
				}
			);
		},
		[]
	);

	const communitiesSettingsChangedHandler = useCallback(
		(model: CommunitiesSettingsModel): void => {
			hierarchyChangedHandler();

			if (!viewModel) {
				return;
			}

			const newViewModel: HubManagementViewModel = {
				...viewModel,
				communities: {
					...viewModel.communities,
					isEnabled: model.isEnabled
				}
			};

			setViewModel(viewModel);

			setAvailableSidebarItems(
				listAvailableSidebarItems(newViewModel)
			);
		},
		[hierarchyChangedHandler, viewModel]
	);

	const renderContent = (): ReactElement | null => {
		if (isNil(selectedView)) {
			return null;
		}

		switch (selectedView) {
			case "announcement.overview":
				return (
					<AnnouncementsOverview
						onParentBackdropChange={setBackdropState}
						isMobile={isMobile}
						isResponsive={isResponsive}
						onItemsChanged={() => itemsChangedHandler(HubItemKind.Announcement)}
					/>
				);
			case "announcement.category":
				return (
					<AnnouncementsCategories
						onParentBackdropChange={setBackdropState}
						onCategoriesChanged={hierarchyChangedHandler}
						isMobile={isMobile}
						isResponsive={isResponsive}
					/>
				);
			case "announcement.template":
				return (
					<TemplateOverview
						kind={HubItemKind.Announcement}
						onParentBackdropChange={setBackdropState}
						isMobile={isMobile}
						isResponsive={isResponsive}
					/>
				);
			case "communities":
				return (
					<CommunitiesContent
						isMobile={isMobile}
						isResponsive={isResponsive}
					/>
				);
			case "communities.settings":
				return (
					<CommunitiesSettings
						onChanged={communitiesSettingsChangedHandler}
						isMobile={isMobile}
						isResponsive={isResponsive}
					/>
				);
			case "communities.membershipRequests":
				return (
					<CommunityMembershipRequests
						isMobile={isMobile}
						isResponsive={isResponsive}
					/>
				);
			case "event.overview":
				return (
					<EventsOverview
						onParentBackdropChange={setBackdropState}
						isMobile={isMobile}
						isResponsive={isResponsive}
						onItemsChanged={() => itemsChangedHandler(HubItemKind.Event)}
					/>
				);
			case "event.category":
				return (
					<EventsCategories
						onParentBackdropChange={setBackdropState}
						isMobile={isMobile}
						isResponsive={isResponsive}
						onCategoriesChanged={hierarchyChangedHandler}
					/>
				);
			case "event.template":
				return (
					<TemplateOverview
						kind={HubItemKind.Event}
						onParentBackdropChange={setBackdropState}
						isMobile={isMobile}
						isResponsive={isResponsive}
					/>
				);
			case "knowledgeItem.overview":
				return (
					<KnowledgeItemsOverview
						onParentBackdropChange={setBackdropState}
						isMobile={isMobile}
						isResponsive={isResponsive}
						onItemsChanged={() => itemsChangedHandler(HubItemKind.KnowledgeItem)}
					/>
				);
			case "knowledgeItem.category":
				return (
					<KnowledgeItemsCategories
						onParentBackdropChange={setBackdropState}
						isMobile={isMobile}
						isResponsive={isResponsive}
						onCategoriesChanged={hierarchyChangedHandler}
					/>
				);
			case "knowledgeItem.template":
				return (
					<TemplateOverview
						kind={HubItemKind.KnowledgeItem}
						onParentBackdropChange={setBackdropState}
						isMobile={isMobile}
						isResponsive={isResponsive}
					/>
				);
			case "settings":
				return (
					<HubSettings
						onParentBackdropChange={setBackdropState}
						isMobile={isMobile}
						isResponsive={isResponsive}
					/>
				);
			default:
				never(selectedView);
		}
	};

	return (
		<Dialog
			variant="left-slider"
			trackingId="hub-management-dialog"
			onClose={closeHandler}
			backdrop={backdropState}
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_ManageTheHub")}
				onClose={closeHandler}
				hasBorder
			/>
			<Dialog.Body goesEdgeToEdge>
				<ContentWithSidebar>
					<ContentWithSidebar.Sidebar ref={sidebarRef}>
						<HubManagementDialogSidebar
							availableItems={availableSidebarItems}
							onSelectedViewChange={handleSelectedViewChange}
							selectedView={selectedView}
						/>
					</ContentWithSidebar.Sidebar>
					<ContentWithSidebar.Content hasNoPadding>
						{renderContent()}
					</ContentWithSidebar.Content>
				</ContentWithSidebar>
			</Dialog.Body>
		</Dialog>
	);
};
HubManagementDialog.displayName="HubManagementDialog";

export type HubManagementDialogInitialData = Omit<HubManagementDialogProps, "onClose">;

export function useHubManagementDialog(): [
	element: ReactElement | null,
	open: (props: HubManagementDialogInitialData) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose) => (
			<HubManagementDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}

function getInitialSelectedView(
	initialView: SelectedView | undefined,
	availableSidebarItems: readonly SidebarItem[]
): SelectedView | undefined {
	const views: SelectedView[] = [];

	for (const item of availableSidebarItems) {
		switch (item.kind) {
			case "view":
				views.push(item.view);
				break;
			case "source":
				views.push(
					...item
						.children
						.map(child => child.view)
				);
				break;
			default:
				never(item);
		}
	}

	if (initialView && views.includes(initialView)) {
		return initialView;
	}

	return views[0];
}